import { useState } from 'react';
import { Control, useController } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  FileIcon,
  FileX,
  Trash,
  UploadIcon,
  X,
  FileText,
  Image as ImageIcon,
  ExternalLink,
} from 'lucide-react';

// Helper function to format bytes to a human-readable format
function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

// Create a JSON string from a file object - this function helps serialize FileData objects
// for storage in the database ARRAY columns
function serializeFileData(fileData: FileData): string {
  return JSON.stringify(fileData);
}

// Parse a JSON string back into a FileData object
function deserializeFileData(jsonStr: string): FileData | null {
  try {
    const parsed = JSON.parse(jsonStr);
    // Validate the parsed object has the required fields
    if (
      parsed &&
      typeof parsed.data === 'string' &&
      typeof parsed.name === 'string' &&
      typeof parsed.type === 'string' &&
      typeof parsed.size === 'number'
    ) {
      return parsed as FileData;
    }
    return null;
  } catch (e) {
    console.error('Error deserializing file data:', e);
    return null;
  }
}

// Structure to store both file data and metadata
export interface FileData {
  data: string; // Base64 data URL or file URL from the server
  name: string; // Original file name for display
  type: string; // File MIME type
  size: number; // File size in bytes
  id?: string | number; // Optional ID when stored on the server
  filePath?: string; // Optional path when stored on the server

  // Additional properties to support server-side format
  filename?: string; // Server-side filename (often generated)
  originalFilename?: string; // Server-side original filename
  mimeType?: string; // Server-side mime type
  fileSize?: number; // Server-side file size
  category?: string; // Server-side document category
  installationId?: number; // Server-side installation ID
  url?: string; // Server-side URL (for API access)
}

// Two different ways to use the component:
// 1. With react-hook-form controller (name, control)
// 2. With direct value/onChange props
interface DocumentUploadControllerProps {
  name: string;
  label?: string;
  description?: string;
  control: Control<any>;
  maxFiles?: number;
  accept?: string;
  value?: never;
  onChange?: never;
}

interface DocumentUploadDirectProps {
  name?: never;
  label?: string;
  description?: string;
  control?: never;
  maxFiles?: number;
  accept?: string;
  value: FileData[];
  onChange: (files: FileData[]) => void;
}

type DocumentUploadProps = DocumentUploadControllerProps | DocumentUploadDirectProps;

export function DocumentUpload(props: DocumentUploadProps) {
  const {
    label,
    description,
    maxFiles = 10, // Default max of 10 files
    accept,
  } = props;

  // Determine which mode we're in and set up files state accordingly
  const isControllerMode = 'name' in props && 'control' in props;

  // State for direct mode
  const [directFiles, setDirectFiles] = useState<FileData[]>(
    'value' in props && props.value ? props.value : []
  );

  // Always call useController with dummy values to satisfy hooks rules
  const controllerConfig =
    isControllerMode && props.name && props.control
      ? { name: props.name, control: props.control, defaultValue: [] }
      : { name: 'dummy', control: {} as Control<any>, defaultValue: [] };

  const controller = useController(controllerConfig);

  const [fileError, setFileError] = useState<string | null>(null);

  // Manage files as array of FileData objects - either from form controller or direct props
  const files: FileData[] =
    isControllerMode && controller ? controller.field.value || [] : directFiles;

  // Get the current installation ID from the URL path
  const getInstallationId = (): number | null => {
    const path = window.location.pathname;
    const matches = path.match(/\/installations\/(\d+)/);
    if (matches && matches[1]) {
      return parseInt(matches[1], 10);
    }
    return null;
  };

  // Function to handle form field updates based on the component mode
  const updateFiles = (newFiles: FileData[]) => {
    if (isControllerMode && controller) {
      controller.field.onChange(newFiles);
    } else if ('onChange' in props && props.onChange) {
      props.onChange(newFiles);
      setDirectFiles(newFiles);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    // Check if adding these files would exceed the max files limit
    if (files.length + fileList.length > maxFiles) {
      setFileError(`You can only upload a maximum of ${maxFiles} files`);
      return;
    }

    // Get the installation ID from the URL
    const installationId = getInstallationId();

    if (!installationId) {
      console.error('Cannot determine installation ID from URL');
      setFileError('Unable to determine installation ID. Please save the form first.');
      return;
    }

    setFileError(null);

    // Process each file
    Array.from(fileList).forEach(async (file) => {
      // Size check: 10MB max
      if (file.size > 10 * 1024 * 1024) {
        setFileError('Files must be smaller than 10MB');
        return;
      }

      // Log file information for debugging
      console.log(`Processing file upload: ${file.name} (${file.type}, ${formatBytes(file.size)})`);

      try {
        // First, convert to base64 so we can upload to the server
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64Data = reader.result as string;

          // Verify we have a valid data URL
          if (!base64Data || !base64Data.startsWith('data:')) {
            console.error('Invalid file data format');
            return;
          }

          // Create a simpler data URL format that our server can process better
          // Use a simpler format: data:mimetype;base64,content
          const mimeType = file.type || 'application/octet-stream';

          // Extract the base64 part
          const base64Match = base64Data.match(/^data:[^;]+;base64,(.+)$/);
          if (!base64Match) {
            console.error('Invalid data URL format');
            return;
          }

          console.log('Creating simplified data URL format');

          // Create a data URL with the filename embedded for better user experience
          // Include the filename to maintain the original name through the entire system
          const encodedFilename = encodeURIComponent(file.name);
          const simpleDataUrl = `data:${mimeType};filename=${encodedFilename};base64,${base64Match[1]}`;

          // Special handling for PDFs to ensure proper MIME type
          let finalDataUrl = simpleDataUrl;
          if (file.name.toLowerCase().endsWith('.pdf') && !mimeType.includes('pdf')) {
            console.log('Converting to PDF MIME type');
            finalDataUrl = `data:application/pdf;filename=${encodedFilename};base64,${base64Match[1]}`;
          }

          console.log(`Data URL format: ${finalDataUrl.substring(0, 50)}...`);

          // Upload the file to the server using the file upload API
          try {
            console.log('==== CLIENT DOCUMENT UPLOAD START ====');
            // Convert form field names to proper OneDrive categories
            let category = 'general'; // Default category
            if (isControllerMode && props.name) {
              // Convert field names like 'technicalDocuments' to category names like 'technical'
              const fieldName = props.name;
              if (fieldName.endsWith('Documents')) {
                category = fieldName.replace('Documents', '').toLowerCase();
              } else {
                category = fieldName.toLowerCase();
              }
            }
            const installId = installationId;
            const url = `/api/installations/${installId}/documents/${category}/upload`;

            console.log(`Installation ID: ${installId}`);
            console.log(`Category: ${category}`);
            console.log(`Filename: ${file.name}`);
            console.log(`File type: ${file.type || 'application/octet-stream'}`);
            console.log(`File size: ${formatBytes(file.size)}`);
            console.log(`API endpoint: ${url}`);

            const payload = {
              dataUrl: finalDataUrl,
              filename: file.name,
              type: file.type || 'application/octet-stream',
            };

            console.log('Making API request to upload document...');
            const response = await fetch(url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(payload),
            });

            if (!response.ok) {
              throw new Error(`Server returned ${response.status}: ${response.statusText}`);
            }

            console.log(`Server response status: ${response.status} ${response.statusText}`);
            const result = await response.json();
            console.log('Server response:', result);

            if (!result.success) {
              console.error('Server indicated upload failure:', result.error);
              throw new Error(result.error || 'Unknown error uploading file');
            }

            console.log('File uploaded successfully:', result.file);

            // Create a more comprehensive FileData object using all available server info
            const fileData: FileData = {
              // Core required FileData properties
              data: result.file.url || `/api/documents/${result.file.id}`, // Use the server-provided URL
              name: file.name,
              type: file.type || 'application/octet-stream',
              size: file.size,

              // Add server-provided details (include everything we have from the server)
              id: result.file.id || result.file.filename, // Prefer actual DB ID
              filePath: result.file.path,

              // Add additional server properties for better compatibility
              filename: result.file.filename,
              originalFilename: file.name,
              mimeType: file.type || 'application/octet-stream',
              fileSize: file.size,
              category: result.file.category,
              installationId: result.file.installationId,
              url: result.file.url,
            };

            // Log the created FileData object
            console.log(`Created FileData from uploaded file: ${fileData.name} (${fileData.type})`);
            console.log(`File path: ${fileData.filePath}`);
            console.log(`File URL: ${fileData.data}`);

            // Add to existing files
            const updatedFiles = [...files, fileData];
            console.log(`Updating form field with ${updatedFiles.length} files`);

            // Update files according to component mode
            updateFiles(updatedFiles);

            console.log('==== CLIENT DOCUMENT UPLOAD COMPLETE ====');
          } catch (uploadError) {
            console.error('Error uploading file to server:', uploadError);
            const errorMessage =
              uploadError instanceof Error ? uploadError.message : 'Unknown error';
            setFileError(`Failed to upload ${file.name}: ${errorMessage}`);
          }
        };

        // Start the file reading process
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error processing file:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        setFileError(`Failed to process ${file.name}: ${errorMessage}`);
      }
    });

    // Clear the input to allow uploading the same file again if needed
    e.target.value = '';
  };

  const removeFile = async (index: number) => {
    // Get the file that's being removed
    const fileToRemove = files[index];
    console.log('Attempting to remove file:', fileToRemove);

    // Check if this file has a filename/ID (meaning it was saved to the server)
    if (fileToRemove && fileToRemove.id) {
      const deleted = await deleteFile(fileToRemove);

      if (!deleted) {
        console.warn('File will be removed from form but may not have been deleted from server');
      } else {
        console.log('File successfully deleted from server and form');
      }
    } else {
      console.log('File has no ID, only removing from form state');
    }

    // Always update the form state by removing the file from the array
    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);

    // Update files according to component mode
    updateFiles(updatedFiles);

    setFileError(null);
  };

  // Handle clearing all files - used by "Clear All" button
  const clearAllFiles = async () => {
    // Delete each file from the server before clearing the form
    if (files.length > 0) {
      console.log(`Attempting to delete ${files.length} files from the server...`);

      const results = await Promise.all(
        files.map(async (file) => {
          return await deleteFile(file);
        })
      );

      const successCount = results.filter(Boolean).length;
      console.log(`Successfully deleted ${successCount} of ${files.length} files from the server`);
    }

    // Clear all files from the form
    updateFiles([]); // Use our common update function
    setFileError(null);
  };

  // Helper function to delete a single file
  const deleteFile = async (file: FileData): Promise<boolean> => {
    if (!file || !file.id) {
      console.log('No file ID to delete');
      return false;
    }

    try {
      // The main filename to use for deletion
      let fileId = file.id ? String(file.id) : '';
      let fileFromUrl = null;

      // Get the filename from data URL if it's not a direct ID reference
      if (file.data && file.data.startsWith('/api/files/')) {
        // Extract the filename from the URL path
        const urlParts = file.data.split('/');
        if (urlParts.length > 0) {
          fileFromUrl = decodeURIComponent(urlParts[urlParts.length - 1]);
          console.log('Extracted filename from URL:', fileFromUrl);
        }
      }

      // Try to find document ID from the database if we have one
      const documentId = parseInt(String(fileId));

      if (!isNaN(documentId)) {
        console.log(`Deleting document with numeric ID: ${documentId} from the server...`);
        // Call the API to delete the document from the server using the document ID
        const response = await fetch(`/api/documents/${documentId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          console.error(`Server error deleting document ID ${documentId}:`, await response.text());

          // If we also have a filename from URL, try that as a fallback
          if (fileFromUrl) {
            console.log(`Falling back to delete by filename: ${fileFromUrl}`);
            const encodedFileName = encodeURIComponent(fileFromUrl);
            const deleteUrl = `/api/files/${encodedFileName}`;

            try {
              const fallbackResponse = await fetch(deleteUrl, {
                method: 'DELETE',
                headers: {
                  'Content-Type': 'application/json',
                },
              });

              if (fallbackResponse.ok) {
                console.log(`File ${fileFromUrl} successfully deleted from server via fallback`);
                return true;
              } else {
                console.error(`Fallback file deletion also failed: ${fileFromUrl}`);
                return false;
              }
            } catch (fallbackError) {
              console.error('Error during fallback file deletion:', fallbackError);
              return false;
            }
          }

          return false;
        } else {
          console.log(`Document ID ${documentId} successfully deleted from server`);
          return true;
        }
      } else {
        // Always prefer the URL-extracted filename if available
        const fileToDelete = fileFromUrl || fileId;
        console.log(`Deleting file with filename: ${fileToDelete}`);

        // Construct a proper encoded URL for the file deletion endpoint
        const encodedFileName = encodeURIComponent(fileToDelete);
        const deleteUrl = `/api/files/${encodedFileName}`;
        console.log(`DELETE request to: ${deleteUrl}`);

        // Try to delete the actual file
        const response = await fetch(deleteUrl, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          console.error(`Server error deleting file ${fileToDelete}:`, await response.text());

          // If we tried with fileFromUrl and it failed, try with fileId as a fallback
          if (fileFromUrl && fileFromUrl !== fileId) {
            console.log(`Trying alternate filename: ${fileId}`);
            const altEncodedFileName = encodeURIComponent(fileId);
            const altDeleteUrl = `/api/files/${altEncodedFileName}`;

            try {
              const altResponse = await fetch(altDeleteUrl, {
                method: 'DELETE',
                headers: {
                  'Content-Type': 'application/json',
                },
              });

              if (altResponse.ok) {
                console.log(`File ${fileId} successfully deleted using alternate filename`);
                return true;
              } else {
                console.error(`Alternate file deletion also failed: ${fileId}`);
                return false;
              }
            } catch (altError) {
              console.error('Error during alternate file deletion:', altError);
              return false;
            }
          }

          return false;
        } else {
          console.log(`File ${fileToDelete} successfully deleted from server`);
          return true;
        }
      }
    } catch (error) {
      console.error('Error during file deletion process:', error);
      return false;
    }
  };

  // Get appropriate icon based on file type and name
  const getFileIcon = (file: FileData) => {
    const fileType = file.type;
    const fileName = file.name || '';

    // If no type is specified, try to infer from filename
    if (!fileType || fileType === 'application/octet-stream') {
      if (fileName.toLowerCase().endsWith('.pdf')) {
        return <FileText className="h-5 w-5 shrink-0 text-primary" />;
      }
      return <FileIcon className="h-5 w-5 shrink-0 text-primary" />;
    }

    // Otherwise use the file type
    if (fileType.startsWith('image/')) {
      return <ImageIcon className="h-5 w-5 shrink-0 text-primary" />;
    } else if (fileType.includes('pdf') || fileName.toLowerCase().endsWith('.pdf')) {
      return <FileText className="h-5 w-5 shrink-0 text-primary" />;
    } else {
      return <FileIcon className="h-5 w-5 shrink-0 text-primary" />;
    }
  };

  // Check if a file is a PDF based on its type and name
  const isPdf = (file: FileData): boolean => {
    return (
      file.type === 'application/pdf' ||
      file.type?.includes('pdf') ||
      file.name?.toLowerCase().endsWith('.pdf') ||
      file.data?.toLowerCase().includes('application/pdf')
    );
  };

  // Open file in new tab for viewing
  const viewFile = (file: FileData) => {
    console.log(`Opening file: ${file.name} (${file.type})`);

    // Determine if we're dealing with a PDF
    const isPdfFile = isPdf(file);

    // Different handling based on data URL format
    if (file.data.startsWith('data:')) {
      console.log('Processing data URL for viewing');

      // For PDFs specifically
      if (isPdfFile && !file.data.includes('application/pdf')) {
        console.log('Converting data URL to PDF format before opening');

        // Create a PDF-compatible data URL from the existing one
        let pdfData = file.data;

        // Extract just the base64 part
        const base64Match = file.data.match(/;base64,(.+)$/);
        if (base64Match && base64Match[1]) {
          // Make sure the filename is included
          pdfData = `data:application/pdf;filename=${encodeURIComponent(file.name)};base64,${base64Match[1]}`;
          console.log('Successfully converted to PDF data URL with filename');
        }

        window.open(pdfData, '_blank');
      } else {
        // Regular data URL handling
        window.open(file.data, '_blank');
      }
    }
    // Handle text content that might be a serialized FileData object
    else if (
      typeof file.data === 'string' &&
      file.data.startsWith('{') &&
      file.data.endsWith('}')
    ) {
      console.log('Attempting to process serialized FileData');
      try {
        // Try to parse it as a JSON string
        const parsedData = JSON.parse(file.data);
        if (parsedData && parsedData.data && parsedData.data.startsWith('data:')) {
          console.log('Successfully extracted data URL from serialized FileData');
          window.open(parsedData.data, '_blank');
          return;
        }
      } catch (err) {
        console.error('Failed to parse serialized FileData:', err);
      }

      // Fallback to opening the string itself
      window.open(file.data, '_blank');
    }
    // Handle URLs to file content (API endpoints, etc.)
    else if (
      typeof file.data === 'string' &&
      (file.data.startsWith('/') || file.data.startsWith('http'))
    ) {
      console.log('Opening URL to file content');
      window.open(file.data, '_blank');
    }
    // Fallback for unknown formats
    else {
      console.warn('Unknown file data format, attempting to open anyway');
      window.open(file.data, '_blank');
    }
  };

  // Extract a viewable image URL from any file data format
  const getImageSrc = (file: FileData): string => {
    // If it's already a data URL with image MIME type, return it directly
    if (file.data.startsWith('data:image/')) {
      return file.data;
    }

    // If it's a serialized JSON object
    if (typeof file.data === 'string' && file.data.startsWith('{') && file.data.endsWith('}')) {
      try {
        const parsed = JSON.parse(file.data);
        if (parsed && parsed.data && typeof parsed.data === 'string') {
          return parsed.data;
        }
      } catch (e) {
        console.error('Error parsing image data JSON:', e);
      }
    }

    // External URL (absolute or relative)
    if (
      typeof file.data === 'string' &&
      (file.data.startsWith('/') || file.data.startsWith('http'))
    ) {
      return file.data;
    }

    // Fallback to raw data
    return file.data;
  };

  // Extract a viewable PDF URL from any file data format
  const getPdfViewUrl = (file: FileData): string => {
    // If it's already a data URL with PDF MIME type, return it directly
    if (file.data.startsWith('data:application/pdf') || file.data.includes('application/pdf')) {
      return `${file.data}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`;
    }

    // If it's a data URL of another type but we know it's a PDF, convert to proper format
    if (
      file.data.startsWith('data:') &&
      (file.type?.includes('pdf') || file.name?.toLowerCase().endsWith('.pdf'))
    ) {
      // Extract base64 content
      const base64Match = file.data.match(/;base64,(.+)$/);
      if (base64Match && base64Match[1]) {
        return `data:application/pdf;base64,${base64Match[1]}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`;
      }
    }

    // If it's a serialized JSON object
    if (file.data.startsWith('{') && file.data.endsWith('}')) {
      try {
        const parsed = JSON.parse(file.data);
        if (parsed && parsed.data && parsed.data.startsWith('data:')) {
          return `${parsed.data}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`;
        }
      } catch (e) {
        console.error('Error parsing file data JSON:', e);
      }
    }

    // Fallback to raw data
    return `${file.data}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`;
  };

  // Format file type for display
  const getFileTypeDisplay = (file: FileData): string => {
    if (!file.type || file.type === 'application/octet-stream') {
      // Try to infer from filename
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (ext === 'pdf') return 'PDF Document';
      if (ext === 'docx') return 'Word Document';
      if (ext === 'xlsx') return 'Excel Spreadsheet';
      if (['jpg', 'jpeg', 'png', 'gif'].includes(ext || '')) return 'Image';
      return 'Document';
    }

    if (file.type.startsWith('image/')) {
      return 'Image';
    } else if (file.type.includes('pdf')) {
      return 'PDF Document';
    } else if (file.type.includes('word') || file.type.includes('document')) {
      return 'Word Document';
    } else if (file.type.includes('excel') || file.type.includes('spreadsheet')) {
      return 'Excel Spreadsheet';
    } else {
      return 'Document';
    }
  };

  // Generate a unique ID for the file input
  const inputId =
    isControllerMode && props.name
      ? `${props.name}-upload`
      : `file-upload-${Math.random().toString(36).substring(2, 9)}`;

  return (
    <FormItem>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <FormLabel>{label}</FormLabel>
          {files.length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={clearAllFiles}
              className="flex items-center gap-1 h-8"
            >
              <Trash className="h-4 w-4" />
              <span>Clear All</span>
            </Button>
          )}
        </div>

        {description && <FormDescription>{description}</FormDescription>}

        <div
          className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center hover:bg-gray-50 cursor-pointer transition-colors relative"
          onClick={() => document.getElementById(inputId)?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
              // Create a synthetic event to pass to handleFileUpload
              const syntheticEvent = {
                target: {
                  files: e.dataTransfer.files,
                  value: '',
                },
              } as React.ChangeEvent<HTMLInputElement>;

              handleFileUpload(syntheticEvent);
            }
          }}
        >
          <div className="flex flex-col items-center justify-center py-4">
            <UploadIcon className="h-10 w-10 mx-auto mb-3 text-gray-400" />
            <p className="text-sm text-gray-600 font-medium mb-1">
              Drop files here or click to browse
            </p>
            <p className="text-xs text-gray-400 mb-3">PDF, DOCX, XLSX, JPG, PNG (max 10MB)</p>
            <Button variant="outline" size="sm" type="button" className="mt-2">
              <UploadIcon className="h-4 w-4 mr-2" />
              Select Files
            </Button>
          </div>
          <FormControl>
            <input
              id={inputId}
              type="file"
              accept={accept || '.pdf,.docx,.xlsx,.jpg,.jpeg,.png'}
              className="hidden"
              multiple
              onChange={handleFileUpload}
            />
          </FormControl>
        </div>

        {fileError && <div className="text-sm text-destructive mt-2">{fileError}</div>}

        {files.length > 0 && (
          <div className="space-y-4 mt-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Uploaded Documents ({files.length})</p>
              <p className="text-xs text-muted-foreground">
                {files.length} of {maxFiles} files
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {files.map((file, index) => (
                <div key={index} className="flex flex-col p-3 bg-muted rounded-md relative">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 absolute top-2 right-2 z-10 bg-background/80 hover:bg-background"
                    onClick={() => removeFile(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  <div className="flex items-center mb-2">
                    <div className="flex items-center gap-2">
                      {getFileIcon(file)}
                      <span className="text-sm truncate max-w-[200px] font-medium">
                        {file.name}
                      </span>
                    </div>
                  </div>

                  {/* Preview for images and PDFs */}
                  {file.type && file.type.startsWith('image/') && (
                    <div className="mt-2 overflow-hidden rounded-md border border-gray-200">
                      <img
                        src={getImageSrc(file)}
                        alt={file.name}
                        className="w-full h-auto max-h-[200px] object-contain bg-white p-2"
                        onClick={() => viewFile(file)}
                      />
                    </div>
                  )}

                  {isPdf(file) && (
                    <div className="mt-2 overflow-hidden rounded-md border border-gray-200 bg-white">
                      <div className="h-[180px] relative">
                        {/* Direct PDF preview integrated */}
                        <iframe
                          src={getPdfViewUrl(file)}
                          className="absolute inset-0 w-full h-full"
                          style={{ border: 'none' }}
                          title={file.name}
                        />

                        {/* Info overlay */}
                        <div className="absolute bottom-0 left-0 right-0 p-2 flex justify-between items-center bg-white/80 backdrop-blur-sm">
                          <div className="flex items-center gap-1">
                            <FileText className="h-4 w-4 text-primary" />
                            <span className="text-xs font-medium truncate max-w-[120px]">
                              {file.name}
                            </span>
                          </div>
                          <span className="text-xs py-0.5 px-2 bg-slate-100 rounded-full text-slate-600">
                            {formatBytes(file.size)}
                          </span>
                        </div>
                      </div>
                      <div className="p-2 border-t">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => viewFile(file)}
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Full View
                        </Button>
                      </div>
                    </div>
                  )}

                  {!file.type?.startsWith('image/') && !isPdf(file) && (
                    <div className="mt-2 overflow-hidden rounded-md border border-gray-200 bg-white">
                      <div className="h-[100px] p-4 flex flex-col items-center justify-center">
                        <FileIcon className="h-10 w-10 text-slate-400 mb-2" />
                        <span className="text-xs text-slate-500">{formatBytes(file.size)}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <FormMessage />
      </div>
    </FormItem>
  );
}
